import { useState, useEffect, useRef, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Send, ChevronDown, Lock, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { MessageAvatar } from '@/components/chat/Messages/Message/Avatar'
import BlinkingCursor from '@/components/BlinkingCursor'
import FormCard from './FormCard'
import { useNavigate } from 'react-router-dom'


interface Message {
  role: 'user' | 'assistant'
  content?: string
  contentAfter?: string
  formCard?: any
  submittedValues?: Record<string, string>
  streaming?: boolean
  isFormSubmission?: boolean
  id?: number
}

interface Props {
  messages: Message[]
  isStreaming: boolean
  onSend: (text: string) => void
  onFormSubmit: (values: Record<string, string>) => void
  initialSubmitted?: Set<number>
  readOnly?: boolean
}

export default function ChatPanel({ messages, isStreaming, onSend, onFormSubmit, initialSubmitted, readOnly }: Props) {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [submitted, setSubmitted] = useState<Set<number>>(() => initialSubmitted || new Set())
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const autoScrollRef = useRef(true)

  const scrollToBottom = useCallback((smooth = false) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' })
  }, [])

  useEffect(() => {
    const el = scrollAreaRef.current
    if (!el) return
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
      autoScrollRef.current = atBottom
      setShowScrollBtn(!atBottom)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (autoScrollRef.current) scrollToBottom(false)
  }, [messages, isStreaming, scrollToBottom])

  const send = () => {
    const t = input.trim()
    if (!t || isStreaming) return
    setInput('')
    autoScrollRef.current = true
    setShowScrollBtn(false)
    onSend(t)
  }

  const handleFormSubmit = (msgId: number, values: Record<string, string>) => {
    setSubmitted(prev => new Set([...prev, msgId]))
    onFormSubmit(values)
  }

  const hasActiveForm = messages.some((m, i) => m.formCard && !submitted.has(i))

  return (
    <div className="flex flex-col h-full gap-3">
      {/* Messages */}
      <div className="relative flex-1 min-h-0 overflow-y-auto" ref={scrollAreaRef}>
        <div className="flex flex-col gap-4 py-2 px-2 pr-3">
          {messages.map((msg, i) => {
            if (msg.role === 'user') {
              if (msg.isFormSubmission) return null
              return (
                <div key={i} className="flex justify-end gap-2">
                  <div className="bg-secondary text-secondary-foreground rounded-2xl px-4 py-2.5 text-sm max-w-[80%] leading-relaxed">
                    {msg.content}
                  </div>
                </div>
              )
            }

            return (
              <div key={i} className="flex gap-2.5 items-start">
                <MessageAvatar />
                <div className="flex flex-col gap-2 flex-1 min-w-0">
                  {(msg.content || (msg.streaming && !msg.formCard)) && (
                    <div className={cn('text-sm text-foreground leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-headings:my-2', msg.streaming && !msg.formCard && '[&>p:last-child]:inline')}>
                      {msg.content ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                      ) : null}
                      {msg.streaming && !msg.formCard && <BlinkingCursor whitespace />}
                    </div>
                  )}
                  {(msg.contentAfter || (msg.streaming && msg.formCard)) && (
                    <div className={cn('text-sm text-foreground leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1', msg.streaming && '[&>p:last-child]:inline')}>
                      {msg.contentAfter ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.contentAfter}</ReactMarkdown>
                      ) : null}
                      {msg.streaming && <BlinkingCursor whitespace />}
                    </div>
                  )}
                  {msg.formCard && (
                    <FormCard
                      card={msg.formCard}
                      onSubmit={vals => handleFormSubmit(i, vals)}
                      submitted={submitted.has(i)}
                      submittedValues={msg.submittedValues}
                    />
                  )}
                </div>
              </div>
            )
          })}

          {isStreaming && !messages.some(m => m.streaming) && (
            <div className="flex gap-2.5 items-start">
              <MessageAvatar />
              <BlinkingCursor />
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {showScrollBtn && (
          <Button
            size="icon"
            variant="outline"
            className="absolute bottom-3 right-3 h-8 w-8 rounded-full shadow-md"
            onClick={() => { autoScrollRef.current = true; setShowScrollBtn(false); scrollToBottom(true) }}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Input */}
      {readOnly ? (
        <div className="shrink-0 w-full rounded-2xl border border-gray-700 bg-black px-4 py-3 text-center text-sm font-bold flex items-center justify-center gap-3 flex-wrap">
          <span className="text-white flex items-center gap-1.5">
            <Lock className="h-4 w-4" />
            Uppgradera för att interagera med detta avtal
          </span>
          <button
            onClick={() => navigate('/dashboard', { state: { activeSection: 'upgrade' } })}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg border border-gray-500 hover:border-white text-white text-xs font-semibold transition-colors hover:bg-gray-800"
          >
            <Zap className="h-3 w-3" />
            Uppgradera nu
          </button>
        </div>
      ) : (
        <div className="shrink-0 rounded-xl border bg-card p-3">
          <div className="flex gap-2 items-end">
            <Textarea
              className="flex-1 min-h-[36px] max-h-[120px] resize-none border-0 bg-transparent p-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
              value={input}
              placeholder={hasActiveForm
                ? 'Fyll i formuläret ovan eller beskriv med egna ord...'
                : 'Skriv ditt svar eller ställ en fråga...'}
              rows={1}
              onChange={e => {
                setInput(e.target.value)
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
              }}
            />
            <Button
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full"
              onClick={send}
              disabled={isStreaming || !input.trim()}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

